import { Input } from "@/components/inputs/input";
import { SearchInputCommand } from "@/components/inputs/search-input-command";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFilters } from "@/hooks/use-filters";
import { useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FilterBookingDataParams,
  FilterBookingFormData,
} from "./filters-booking.types";
import { startOfMonth } from "date-fns";
import { BOOKING_TYPE } from "@/constants/booking-type";

const bookingTypeOptions = [
  {
    id: BOOKING_TYPE.PRIVATE_OFFICE_CROSS_PRODUCT,
    name: "Escritório Privativo",
  },
  { id: BOOKING_TYPE.EXTRA_SERVICE, name: "Serviço Extra" },
];

export const FiltersBooking = ({
  updateFilters,
}: {
  updateFilters: (data: FilterBookingFormData) => void;
}) => {
  const isInitialSetup = useRef(true);
  // const [expandFilter, setExpandFilter] = useState(false);
  const [searchParams] = useSearchParams();
  const {
    handSelectedCity,
    handSelectedStatus,
    handSelectedCompany,
    handSelectedSpace,
    setFiltersDates,
    filterCities,
    filterStatus,
    filterCompanies,
    filterSpaces,
  } = useFilters();
  // const [clear, setClear] = useState(false);

  const { control, setValue, clearErrors, handleSubmit, reset, watch } =
    useForm<FilterBookingFormData>({
      mode: "onBlur",
      reValidateMode: "onChange",
      defaultValues: {
        start_date: setFiltersDates(startOfMonth(new Date())),
        end_date: setFiltersDates(
          startOfMonth(
            new Date(new Date().setMonth(new Date().getMonth() + 2)),
          ),
        ),
        po_contract_id: undefined,
      },
    });

  useEffect(() => {
    const poContractId = searchParams.get("po_contract_id");

    if (poContractId && !!isInitialSetup.current) {
      isInitialSetup.current = false;
      setValue("po_contract_id", Number(poContractId));
    }
  }, [searchParams, setValue]);

  const selectOptionProp = (
    name: string,
    item: { id: number; name: string },
  ) => {
    const nameKey = name as keyof FilterBookingDataParams;

    clearErrors();
    setValue(nameKey, item);
  };

  const clearFilters = () => {
    // setClear(true);
    reset();
    window.location.reload();
  };

  useEffect(() => {
    let ignore = false;
    let timer: null | ReturnType<typeof setTimeout> = null;

    watch(() => {
      if (timer) clearTimeout(timer);

      timer = setTimeout(() => {
        handleSubmit(updateFilters)();
      }, 1000);
    });

    if (!ignore) {
      handSelectedStatus("STATUS_BOOKINGS", 7);
    }
    return () => {
      ignore = true;
    };
  }, [handleSubmit, watch]);

  return (
    <form onSubmit={handleSubmit(updateFilters)}>
      <div className="bg-white px-8 pt-0 pb-4 mt-0 border-b">
        <div className="flex items-end justify-between">
          <div className="flex gap-5 flex-col w-full">
            <div className="flex gap-5 items-end w-full flex-wrap">
              <div className="relative filters flex-auto max-w-[200px]">
                <Controller
                  control={control}
                  name="spaces"
                  render={({ field }) => (
                    <SearchInputCommand
                      name="spaces"
                      selectOption={selectOptionProp}
                      searchValue={field.value?.name as string}
                      optionsList={filterSpaces!}
                      updateList={handSelectedSpace}
                      label="Selecionar espaço"
                      inputWidth="w-full"
                      // clear={clear}
                    />
                  )}
                />
              </div>
              <Controller
                name="po_contract_id"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id="po_contract_id"
                    className="shadow-none ring-gray-200 placeholder:text-black min-w-[135px]"
                    label="PO Contract ID"
                    name="po_contract_id"
                    type="number"
                    placeholder="ID do contrato"
                    error={fieldState.error ? fieldState.error.message : ""}
                    showlabel={false}
                    onWheel={(e) => e.currentTarget.blur()}
                    onBlur={(event) => {
                      const value = event.target.value;
                      if (value === "") {
                        field.onChange(undefined);
                      } else {
                        const numValue = Number(value);
                        if (!isNaN(numValue)) {
                          field.onChange(numValue);
                        }
                      }
                    }}
                  />
                )}
              />
              <div className="relative filters flex-auto max-w-[200px]">
                <Controller
                  control={control}
                  name="companies"
                  render={({ field }) => (
                    <SearchInputCommand
                      name="companies"
                      selectOption={selectOptionProp}
                      searchValue={field.value?.name as string}
                      optionsList={filterCompanies!}
                      updateList={handSelectedCompany}
                      label="Selecionar empresa"
                      inputWidth="w-full"
                      // clear={clear}
                    />
                  )}
                />
              </div>
              <div className="relative filters flex-auto max-w-[200px]">
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <SearchInputCommand
                      name="status"
                      selectOption={selectOptionProp}
                      searchValue={field.value?.name as string}
                      optionsList={filterStatus!}
                      updateList={handSelectedStatus}
                      label="Selecionar status"
                      searchDisable={true}
                      inputWidth="w-full"
                      // clear={clear}
                    />
                  )}
                />
              </div>

              <div className="relative filters flex-auto max-w-[200px]">
                <Controller
                  control={control}
                  name="types"
                  render={({ field }) => (
                    <Select
                      value={field.value?.[0]?.toString()}
                      onValueChange={(value) => {
                        const selectedId = parseInt(value);
                        setValue("types", [selectedId]);
                      }}
                    >
                      <SelectTrigger className="h-[38px]">
                        <SelectValue placeholder="Selecionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {bookingTypeOptions.map((option) => (
                          <SelectItem
                            key={option.id}
                            value={option.id.toString()}
                          >
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="relative filters flex-auto max-w-[180px]">
                <Controller
                  control={control}
                  name="cities"
                  render={({ field }) => (
                    <SearchInputCommand
                      name="cities"
                      selectOption={selectOptionProp}
                      searchValue={field.value?.name as string}
                      optionsList={filterCities!}
                      updateList={handSelectedCity}
                      label="Selecionar cidade"
                      inputWidth="w-full"
                    />
                  )}
                />
              </div>

              <Controller
                name="start_date"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id="start_date"
                    label="Start date"
                    name="start_date"
                    type="date"
                    value={field.value}
                    onChange={(event) => {
                      field.onChange(event);
                    }}
                    error={fieldState.error ? fieldState.error.message : ""}
                    required
                  />
                )}
              />

              <Controller
                control={control}
                name="end_date"
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id="end_date"
                    label="End date"
                    name="end_date"
                    type="date"
                    value={field.value}
                    onChange={(event) => {
                      field.onChange(event);
                    }}
                    error={fieldState.error ? fieldState.error.message : ""}
                    required
                  />
                )}
              />

              <div className="gap-3 flex">
                <Button
                  className="min-h-[38px]"
                  variant="outline"
                  type="button"
                  onClick={clearFilters}
                >
                  Limpar filtros
                </Button>
              </div>
            </div>
          </div>
          {/* <div className="gap-3 flex mt-7">
            <Button
              className="min-h-[38px] bg-transparent text-black shadow-none font-bold hover:bg-transparent hover:underline"
              type="button"
              onClick={() => setExpandFilter(!expandFilter)}
            >
              {expandFilter ? "Fechar filtros" : "+ Filtros"}
            </Button>
          </div> */}
        </div>
      </div>
    </form>
  );
};
